import React from "react";
import Dialog from "@mui/material/Dialog";
import { Form, Formik, FormikHelpers } from "formik";
import { array, number, object, string } from "yup";
import { AdvancePaymentFormValues } from "src/models/AdvancePayment.model";
import AdvancePayment from "../Layouts/AdvancePayment";
import { useAddAdvancePaymentMutation } from "src/services/AdvancePaymentService";
import { showToast } from "src/utils/showToaster";
import useDrawer from "src/hooks/useDrawer";
import usePaymentModes from "src/hooks/usePaymentModes";

type Props = {
  onClose: () => void;
};

const AddAdvancePaymentWrapper = ({ onClose }: Props) => {
  const { drawer} = useDrawer();
  const { paymentMode} = usePaymentModes();
  const getPaymentModes = (type: "PHYSICAL" | "VIRTUAL") => {
    return (
      paymentMode
        ?.filter((pm: any) => pm?.type === type)
        .map((pm: any) => ({
          label: pm?.name,
          value: pm?.value,
          type: pm?.type,
        })) || []
    );
  };

  const virtualPaymentModes = getPaymentModes("VIRTUAL");

  const physicalModes =
    drawer?.filter((mode: any) => mode?.type === "PHYSICAL") || [];
  // Form Initial Values
  const initialValues: AdvancePaymentFormValues = {
    name: "",
    mobile: "",
    userType: "STUDENT",
    paymentMode: [
      ...physicalModes?.map((mode: any) => ({
        name: mode.name,
        value: mode.value,
        quantity: "",
        type: mode.type,
      })),
    ],
    remark: "",
    amount: "",
  };

  // Validation Schema
  const validationSchema = object().shape({
    name: string().required("Please enter name"),
    mobile: string().required("Please enter mobile number"),
    remark: string().required("Please enter remark"),
    paymentMode: array().of(
      object().shape({
        name: string().required("Please enter name"),
        value: number().required("Please enter value"),
      })
    ),
    amount: string().required("Please enter amount"),
  });

  const [addAdvancePayment] = useAddAdvancePaymentMutation();

  // Handle Submit
  const handleSubmit = (
    values: AdvancePaymentFormValues,
    { setSubmitting, resetForm }: FormikHelpers<AdvancePaymentFormValues>
  ) => {
    const totalModeAmount = values.paymentMode.reduce((acc, curr) => {
      return acc + (curr.quantity || 0) * (curr.value || 0);
    }, 0);
    const totalAmount = values.amount ? Number(values.amount) : 0;
    if (totalModeAmount !== totalAmount) {
      showToast("error", "Total amount and note must be equal.");
      return setSubmitting(false);
    }
    const formattedValues = {
      paymentMode: values?.paymentMode
        ?.filter((itm) => itm.quantity !== "")
        .map((item) => ({
          name: item?.name,
          value: item?.value,
          type: item?.type,
          quantity: Number(item?.quantity),
        })),
      name: values?.name,
      mobile: values?.mobile,
      amount: Number(values?.amount),
      userType: values?.userType.value,
      remark: values?.remark,
    };
    addAdvancePayment(formattedValues).then((res: any) => {
      if (res?.error) {
        showToast("error", res?.error?.data?.message);
      } else {
        if (res?.data?.status) {
          showToast("success", res?.data?.message);
          resetForm();
          onClose();
        } else {
          showToast("error", res?.data?.message);
        }
      }
      setSubmitting(false);
    });
  };
  
  return (
    <Dialog open maxWidth="md" fullWidth>
      <Formik
      enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formikProps) => (
          <Form>
            <AdvancePayment
              onlineMode={virtualPaymentModes}
              formikProps={formikProps}
              onClose={onClose}
              formType="ADD"
            />
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddAdvancePaymentWrapper;
